import * as THREE from 'three'
import { Clock, PerspectiveCamera, Scene, Vector3, type WebGLRenderer } from 'three'
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js'

import { Generators } from './Generators.ts'
import { CameraShake } from './CameraShake.ts'
import { SceneEnvironmentManager } from './SceneEnvironmentManager.ts'
import { type ThemeManager } from './ThemeManager.ts'
import { type CustomViewHelper } from './CustomViewHelper.ts'
import { type MeshDragBonePlacement } from './processes/edit-skeleton/MeshDragBonePlacement.ts'

/**
 * Core three.js viewport: scene, camera, renderer, transform controls, render
 * loop, environment, view helper. Owns nothing wizard-specific — the main
 * engine composes a Viewport, and the retarget app composes its own Viewport
 * directly (without dragging in step machinery).
 *
 * Per-frame callbacks let consumers (the engine) hook step-specific update
 * work (e.g. AnimationsListing.frame_change) without Viewport knowing about
 * steps.
 */
export class Viewport {
  public readonly scene: Scene = new Scene()
  public readonly camera: PerspectiveCamera = Generators.create_camera()
  // `preserveDrawingBuffer: true` lets us call `renderer.domElement.toDataURL()`
  // outside the render call (used for per-clip thumbnails). Small fixed
  // overhead per frame on integrated GPUs; invisible at typical framerates.
  public readonly renderer: WebGLRenderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true })
  public readonly transform_controls: TransformControls
  public readonly clock: Clock = new Clock()
  private readonly camera_shake: CameraShake
  private scene_environment: SceneEnvironmentManager | undefined
  public view_helper: CustomViewHelper | undefined

  private readonly frame_callbacks: Array<(delta: number) => void> = []
  private started: boolean = false

  constructor () {
    this.transform_controls = new TransformControls(this.camera, this.renderer.domElement)
    this.camera_shake = new CameraShake(this.camera)
    this.animate = this.animate.bind(this)
  }

  /**
   * Initialize the renderer + scene environment (lights, orbit controls, floor,
   * view helper). Must be called once before `start()`. Separated from the
   * constructor because `SceneEnvironmentManager` needs a `theme_manager` and
   * a `mesh_drag_bone_placement` reference, which the engine sets up after
   * the Viewport is constructed.
   */
  public setup_environment (theme_manager: ThemeManager, mesh_drag_bone_placement: MeshDragBonePlacement): void {
    this.scene_environment = new SceneEnvironmentManager(
      this.scene,
      this.renderer,
      this.camera,
      this.transform_controls,
      theme_manager,
      mesh_drag_bone_placement
    )
    this.scene_environment.setup_environment()
    this.view_helper = this.scene_environment.get_view_helper()
  }

  public get_scene_environment (): SceneEnvironmentManager {
    if (this.scene_environment === undefined) {
      throw new Error('Viewport.setup_environment() must be called before accessing scene_environment')
    }
    return this.scene_environment
  }

  /** Begin the render loop. Idempotent. */
  public start (): void {
    if (this.started) return
    this.started = true
    this.animate()
  }

  /** Register a per-frame callback. Returns an unsubscribe function. */
  public on_frame (cb: (delta: number) => void): () => void {
    this.frame_callbacks.push(cb)
    return () => {
      const idx = this.frame_callbacks.indexOf(cb)
      if (idx >= 0) this.frame_callbacks.splice(idx, 1)
    }
  }

  // --- pass-through controls (kept so engine + retarget can both delegate) ---

  public set_camera_position (position: Vector3): void {
    this.get_scene_environment().set_camera_position(position)
  }

  public shake_camera (intensity?: number, duration?: number): void {
    this.camera_shake.start(intensity, duration)
  }

  public set_zoom_limits (min_distance: number, max_distance: number): void {
    this.get_scene_environment().set_zoom_limits(min_distance, max_distance)
  }

  public set_fog_enabled (enabled: boolean): void {
    this.get_scene_environment().set_fog_enabled(enabled)
  }

  public enable_orbit_controls (enabled: boolean): void {
    this.get_scene_environment().enable_orbit_controls(enabled)
  }

  public regenerate_floor_grid (): void {
    this.get_scene_environment().regenerate_floor_grid()
  }

  /**
   * Snapshot the current renderer canvas as a small data URL. Sized down so
   * thumbnails can be persisted without bloating IDB. Returns undefined if
   * the canvas isn't ready or capture fails.
   */
  public capture_canvas_thumbnail (width: number, height: number): string | undefined {
    try {
      const src = this.renderer.domElement
      this.renderer.render(this.scene, this.camera)
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      if (ctx === null) return undefined
      ctx.drawImage(src, 0, 0, width, height)
      return canvas.toDataURL('image/jpeg', 0.7)
    } catch (err) {
      console.warn('capture_canvas_thumbnail failed:', err)
      return undefined
    }
  }

  private animate (): void {
    requestAnimationFrame(this.animate)
    const delta_time = this.clock.getDelta()

    this.camera_shake.update(delta_time)
    for (const cb of this.frame_callbacks) cb(delta_time)

    // Manual clear is required because `autoClear = false` is set so the view
    // helper can layer on top of the main render without wiping it. With
    // `preserveDrawingBuffer: true`, the back buffer is preserved across frame
    // swaps — without an explicit clear, previous frames stack on top of each
    // other and produce ghost trails behind the model.
    this.renderer.clear()
    this.renderer.render(this.scene, this.camera)

    this.view_helper?.render(this.renderer)
    if (this.view_helper?.is_animating() === true) {
      this.view_helper.update(delta_time)
    }
  }
}
